import { Plus, AlertCircle, Ban } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { PermissionButton } from "@/components/ui/permission-button";
import { getPlanTypeMeta, getPlanTypeAccent } from "@shared/schemas/planTypes";
import { cn } from "@/lib/utils";
import { getTypeIcon } from "./typeIcons";

interface EmptyTypeRowProps {
  planType: string;
  /** How many plans the client offers for this type. */
  availablePlanCount: number;
  onConfigure: () => void;
  /** When true, hide the "Configure {short}" CTA. Used by the employee /benefits read-only view. */
  readOnly?: boolean;
  /** Set when the employee is eligible for none of this type's plans: shows "Ineligible" + reason, hides the CTA. */
  ineligibleReason?: string | null;
}

export function EmptyTypeRow({ planType, availablePlanCount, onConfigure, readOnly = false, ineligibleReason }: EmptyTypeRowProps) {
  const meta = getPlanTypeMeta(planType);
  const accent = getPlanTypeAccent(planType);
  const Icon = getTypeIcon(planType);
  const planLabel = availablePlanCount === 1 ? "plan" : "plans";

  return (
    <div className={cn(
      "rounded-lg border border-dashed border-stone-300 dark:border-stone-700",
      "bg-white dark:bg-stone-900",
      "border-l-4 border-l-amber-400 dark:border-l-amber-500",
      "px-4 py-3 flex flex-wrap items-center gap-3",
    )}>
      <div className={cn("h-7 w-7 rounded-md flex items-center justify-center shrink-0 opacity-80", accent.accentBg)}>
        <Icon className={cn("h-4 w-4", accent.accentText)} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {ineligibleReason ? (
            <Badge
              variant="outline"
              className="gap-1 bg-red-50 text-red-800 border-red-200 dark:bg-red-950/30 dark:text-red-200 dark:border-red-800/60"
            >
              <Ban className="h-3 w-3" />
              Ineligible
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="gap-1 bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-800/60"
            >
              <AlertCircle className="h-3 w-3" />
              No election
            </Badge>
          )}
        </div>
        <div className="text-xs text-stone-600 dark:text-stone-400 mt-1">
          {ineligibleReason
            ? `Not eligible for benefits of this type: ${ineligibleReason}`
            : `${availablePlanCount} ${planLabel} available · choose one to enroll or waive.`}
        </div>
      </div>
      {!readOnly && !ineligibleReason && (
        <PermissionButton
          permission="clients:edit"
          whenDenied="hide"
          variant="outline"
          size="sm"
          className="gap-1.5 ml-auto"
          onClick={onConfigure}
        >
          <Plus className="h-3.5 w-3.5" />
          Configure {meta.short}
        </PermissionButton>
      )}
    </div>
  );
}
